import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Section, SectionHeading } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const [info, setInfo] = useState(null);
  const [portraitFailed, setPortraitFailed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/author`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  if (!info) {
    return (
      <div className="page-wrapper pt-40 px-6">
        <div className="overline animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper pt-32 md:pt-40" data-testid="about-page">
      <Section>
        <SectionHeading
          overline="About the Author"
          title={info.tagline}
          kicker={info.short_bio}
        />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-5 md:row-span-2"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#2B3A4A]">
              {portraitFailed ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #1A1D24 0%, #2B3A4A 60%, rgba(212,175,55,0.15) 100%)",
                  }}
                  data-testid="portrait-fallback"
                >
                  <div className="overline mb-3">Portrait</div>
                  <div className="font-serif text-3xl text-[#F4F4F6]">Ellie Stone</div>
                </div>
              ) : (
                <img
                  src={info.portrait}
                  alt={info.name}
                  onError={() => setPortraitFailed(true)}
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
                  style={{ filter: "contrast(1.05) brightness(0.85)" }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D24]/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="overline mb-1">Ellie Stone</div>
                <div className="font-serif text-2xl text-[#F4F4F6]">Author · Weaver of Shadows and Spells</div>
              </div>
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="md:col-span-7 bg-[#2B3A4A] p-10 md:p-14 rounded-2xl border border-[#D4AF37]/5"
          >
            <div className="overline mb-6">The Long Version</div>
            <p className="text-[#A0A4AE] text-base md:text-lg leading-relaxed whitespace-pre-line">
              {info.long_bio}
            </p>
          </motion.div>

          {/* Fun facts */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="md:col-span-7 grid grid-cols-2 gap-4"
          >
            {info.fun_facts.map((fact, i) => (
              <div
                key={i}
                data-testid={`fun-fact-${i}`}
                className="glass-card p-7 md:p-9"
              >
                <div className="font-serif text-5xl md:text-6xl text-[#D4AF37] font-light leading-none mb-3">
                  {fact.value}
                </div>
                <div className="overline">{fact.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>
    </div>
  );
}
